// Firm detection utilities based on hostname

import { HARDCODED_PARAMS } from "@/shared/config/hardcodedParams";

export type FirmDomain = "pregodonnell" | "bpmlaw" | "tysonmendes" | "taylorday" | "namanhowell" | "gouldratner" | "hourglass" | "unknown";

/**
 * Detects the current firm based on window.location.hostname
 */
export function getFirmDomain(): FirmDomain {
  const hostname = window.location.hostname;

  if (hostname.includes("pregodonnell")) return "pregodonnell";
  if (hostname.includes("bpmlaw")) return "bpmlaw";
  if (hostname.includes("tysonmendes")) return "tysonmendes";
  if (hostname.includes("taylorday")) return "taylorday";
  if (hostname.includes("namanhowell")) return "namanhowell";
  if (hostname.includes("gouldratner")) return "gouldratner";

  // Local dev: allow env override since localhost can't be detected by hostname
  if (hostname.includes("localhost")) {
    const validDomains: FirmDomain[] = ["pregodonnell", "bpmlaw", "tysonmendes", "taylorday", "namanhowell", "gouldratner", "hourglass"];
    const envOverride = import.meta.env.VITE_FIRM_DOMAIN;
    if (envOverride && validDomains.includes(envOverride as FirmDomain)) return envOverride as FirmDomain;
    return "hourglass";
  }

  if (hostname.includes("hourglass")) return "hourglass";

  return "unknown";
}

/**
 * Returns true if the current firm is Preg O'Donnell
 */
export function isPregODonnell(): boolean {
  return getFirmDomain() === "pregodonnell";
}

/**
 * Returns true if the current firm is Taylor Day
 */
export function isTaylorDay(): boolean {
  return getFirmDomain() === "taylorday";
}

/**
 * Returns true if the current firm is Naman Howell
 */
export function isNamanHowell(): boolean {
  return getFirmDomain() === "namanhowell";
}

/**
 * Returns true if the current firm is Tyson Mendes
 */
export function isTysonMendes(): boolean {
  return getFirmDomain() === "tysonmendes";
}

/**
 * Returns true if the current firm is Gould Ratner
 */
export function isGouldratner(): boolean {
  return getFirmDomain() === "gouldratner";
}

/**
 * First day of the week for calendar views.
 * Tyson Mendes = 1 (Monday); all other firms = 0 (Sunday).
 */
export function getWeekStartsOn(): 0 | 1 {
  return HARDCODED_PARAMS[getFirmDomain()].weekStartsOn;
}

/**
 * Default task_group and activity_group per firm.
 * Used when no matter is selected or matter has no group assigned.
 * `undefined` means "show all codes" (no filtering).
 */
const FIRM_DEFAULTS: Record<FirmDomain, {
  taskGroup?: string;
  activityGroup?: string;
  /** When true, activity picker disables if the matter's activity_group is NULL. When false/unset, activity is always required. */
  activityOptionalWhenNoGroup?: boolean;
  /** When true, task picker disables if the matter's task_group is NULL. When false/unset, task is always required. */
  taskOptionalWhenNoGroup?: boolean;
}> = {
  taylorday:    { taskGroup: "9a69ea86-d958-482c-b2fd-35c6ee47830d", activityGroup: "dca1c471-21cb-4c00-8e62-d075d854446d", taskOptionalWhenNoGroup: true, activityOptionalWhenNoGroup: true },
  pregodonnell: { activityGroup: "ABA", activityOptionalWhenNoGroup: true },
  namanhowell:  { taskGroup: "54bc344d-0b93-93e4-c912-69a39322a816", taskOptionalWhenNoGroup: true },
  bpmlaw:       { taskGroup: "ABA", activityGroup: "dca1c471-21cb-4c00-8e62-d075d854446d", taskOptionalWhenNoGroup: true, activityOptionalWhenNoGroup: true },
  tysonmendes:  { taskGroup: "3d979d69-0e53-417a-a233-f94800bf123d", taskOptionalWhenNoGroup: true },
  gouldratner:  { taskOptionalWhenNoGroup: true },
  hourglass:    {},
  unknown:      {},
};

export function getFirmDefaults() {
  return FIRM_DEFAULTS[getFirmDomain()];
}

/**
 * Determines if activity code is required for a given matter.
 * - Naman Howell + Gould Ratner: required when matter has task_group (activity_group is never populated for these firms)
 * - Firms with a default activity group: required when matter has activity_group, optional when NULL
 * - Firms without a default: always required
 */
export function isActivityRequired(
  matterId: string | undefined,
  matters: { matter_id: string; activity_group: string; task_group?: string }[]
): boolean {
  // Naman Howell + Gould Ratner: activity required based on task_group, not activity_group
  if (isNamanHowell() || isGouldratner()) {
    if (!matterId) return true;
    const selected = matters.find((m) => m.matter_id === matterId);
    if (!selected) return true;
    return !!(selected.task_group && selected.task_group.trim() !== "");
  }

  const defaults = getFirmDefaults();

  // Only optional when the firm explicitly opts in AND the matter has no group
  if (!defaults.activityOptionalWhenNoGroup) return true;

  // No matter selected — required (show default group)
  if (!matterId) return true;

  const selected = matters.find((m) => m.matter_id === matterId);
  if (!selected) return true;

  // Required when matter has an activity_group, optional when NULL
  return !!(selected.activity_group && selected.activity_group.trim() !== "");
}

/**
 * Determines if task code is required for a given matter.
 * - Firms with a default task group: required when matter has task_group, optional when NULL
 * - Firms without a default: always required
 */
export function isTaskRequired(
  matterId: string | undefined,
  matters: { matter_id: string; task_group: string }[]
): boolean {
  const defaults = getFirmDefaults();

  // Only optional when the firm explicitly opts in AND the matter has no group
  if (!defaults.taskOptionalWhenNoGroup) return true;

  // No matter selected — required (show default group)
  if (!matterId) return true;

  const selected = matters.find((m) => m.matter_id === matterId);
  if (!selected) return true;

  // Required when matter has a task_group, optional when NULL
  return !!(selected.task_group && selected.task_group.trim() !== "");
}
